import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Info, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { STATUT_COLORS, STATUT_LABELS } from '@/lib/statutsConfig';
import {
  STATUT_PAIEMENT_DEPOT_LABELS,
  STATUT_PAIEMENT_DEPOT_COLORS,
  STATUT_PAIEMENT_FINAL_LABELS,
  STATUT_PAIEMENT_FINAL_COLORS,
} from '@/lib/statutsPaiementLabels';
import {
  hideLegendeBadgesCRM,
  LEGENDE_BADGES_CRM_KEY_HIDDEN,
  LEGENDE_BADGES_CRM_KEY_COLLAPSED,
} from './LegendeBadgesCRMHelpers';

// Re-export pour rétrocompatibilité (le parent peut importer ces helpers depuis ce fichier)
export {
  isLegendeBadgesCRMHidden,
  isLegendeBadgesCRMCollapsed,
  setLegendeBadgesCRMCollapsed,
  reopenLegendeBadgesCRM,
} from './LegendeBadgesCRMHelpers';

/**
 * 🆕 [Phase 85 Sprint 1.B — 2026-06-23] Légende repliable des 3 dimensions de statuts CRM.
 *
 * Affiche au-dessus de la liste Dossiers (page CRM → Tous les Projets) une carte
 * qui explique :
 *   1. Statut vente / production (palette legacy)
 *   2. Paiement dépôt 💰 (4 états actifs, le 5e "non_requis" est masqué par défaut)
 *   3. Paiement final 💳 (4 états actifs, le 5e "non_applicable" est masqué par défaut)
 *
 * Comportement :
 *  - Ouverte par défaut au 1er chargement (utile pour la 1re semaine en prod)
 *  - Toggle expand/collapse via icônes ChevronUp/Down (état persisté dans
 *    `localStorage.legende-badges-crm-collapsed` = "1" ou "0")
 *  - Bouton "Ne plus afficher" met `localStorage.legende-badges-crm-hidden = "1"`
 *    → le composant ne rend rien au prochain chargement
 *  - Si masquée définitivement, une mini-icône Info en haut à droite (rendue par
 *    le parent via prop callback) peut permettre de la réactiver. Ce composant
 *    expose `LegendeBadgesCRM.canReopen()` + `LegendeBadgesCRM.reopen()` (helpers
 *    statiques) pour que le parent décide où afficher le bouton de réouverture.
 *
 * Accessibilité : aria-expanded, aria-label sur les boutons.
 */

const KEY_HIDDEN = LEGENDE_BADGES_CRM_KEY_HIDDEN;
const KEY_COLLAPSED = LEGENDE_BADGES_CRM_KEY_COLLAPSED;

// Liste des statuts vente importants à afficher dans la légende (les 7 plus utilisés)
const STATUTS_VENTE_LEGENDE = [
  'prospect',
  'soum_a_faire',
  'soum_a_valider',
  'soumission_envoyee',
  'accepte',
  'en_production',
  'finalise',
];

// Statuts paiement actifs (sans non_requis / non_applicable qui sont des "états caches")
const STATUTS_DEPOT_LEGENDE = ['facture_a_envoyer', 'facture_envoyee', 'partiel', 'paye'];
const STATUTS_FINAL_LEGENDE = ['facture_a_envoyer', 'facture_envoyee', 'partiel', 'paye'];


export function LegendeBadgesCRM() {
  // 1. Si masquée définitivement → ne pas rendre
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(KEY_HIDDEN) === '1'; } catch (_) { return false; }
  });
  // 2. État collapsed/expanded (persistant)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(KEY_COLLAPSED) === '1'; } catch (_) { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(KEY_COLLAPSED, collapsed ? '1' : '0'); } catch (_) {}
  }, [collapsed]);

  const handleHide = () => {
    try { localStorage.setItem(KEY_HIDDEN, '1'); } catch (_) {}
    setHidden(true);
  };

  if (hidden) return null;

  return (
    <Card
      className="mb-4 border-indigo-200 bg-indigo-50/40 dark:bg-slate-900/40"
      data-testid="legende-badges-crm"
    >
      <CardContent className="py-3 px-4">
        {/* Header : titre + chevron + close */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-indigo-900 hover:text-indigo-700"
            aria-expanded={!collapsed}
            data-testid="legende-toggle-btn"
          >
            <Info className="w-4 h-4" aria-hidden="true" />
            Légende des 3 badges statuts CRM
            {collapsed ? (
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ChevronUp className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleHide}
            className="text-slate-500 hover:text-slate-700 h-7 px-2"
            aria-label="Ne plus afficher la légende"
            data-testid="legende-hide-btn"
          >
            <X className="w-3 h-3 mr-1" aria-hidden="true" />
            <span className="text-xs">Ne plus afficher</span>
          </Button>
        </div>

        {/* Corps collapsible : 3 colonnes */}
        {!collapsed && (
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-indigo-100"
            data-testid="legende-content"
          >
            {/* Colonne 1 : Statut vente/production */}
            <div data-testid="legende-col-vente">
              <h4 className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">
                Statut vente / production
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {STATUTS_VENTE_LEGENDE.map((s) => (
                  <Badge
                    key={s}
                    className={STATUT_COLORS[s] || 'bg-slate-100 text-slate-700'}
                    data-testid={`legende-badge-vente-${s}`}
                  >
                    {STATUT_LABELS[s] || s}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2 italic">
                Statut principal du dossier dans le pipeline commercial.
              </p>
            </div>

            {/* Colonne 2 : Paiement dépôt */}
            <div data-testid="legende-col-depot">
              <h4 className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">
                <span aria-hidden="true">💰</span> Paiement dépôt
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {STATUTS_DEPOT_LEGENDE.map((s) => (
                  <Badge
                    key={s}
                    variant="outline"
                    className={`${STATUT_PAIEMENT_DEPOT_COLORS[s]} border`}
                    data-testid={`legende-badge-depot-${s}`}
                  >
                    {STATUT_PAIEMENT_DEPOT_LABELS[s]}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2 italic">
                Masqué si aucune facture dépôt n'a été créée.
              </p>
            </div>

            {/* Colonne 3 : Paiement final */}
            <div data-testid="legende-col-final">
              <h4 className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">
                <span aria-hidden="true">💳</span> Paiement final
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {STATUTS_FINAL_LEGENDE.map((s) => (
                  <Badge
                    key={s}
                    variant="outline"
                    className={`${STATUT_PAIEMENT_FINAL_COLORS[s]} border`}
                    data-testid={`legende-badge-final-${s}`}
                  >
                    {STATUT_PAIEMENT_FINAL_LABELS[s]}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2 italic">
                Masqué tant que le dossier n'est pas finalisé.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
